import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { ChevronLeft } from 'lucide-react-native';
import { makeThemedStyles, DISPLAY_FONT, useV10 } from '../v10/theme';
import { GlassFill } from './glass';
import { QuickLinkButtons } from './quickLinks';

/** Header for every screen opened outside the v10 canvas (tools from the menu,
 *  sessions, ORACLE…). Left: menu + back. Right: the same quick links the canvas
 *  shows — Home, then the user's picks from Settings → Quick links. */
export function OlmpsHeader({
  title,
  onBack,
  onOpenDrawer,
  onHome,
  onTalk,
  onCapture,
  onOpenTool,
  onOpenSettings,
}: {
  title: string;
  onBack: () => void;
  onOpenDrawer: () => void;
  onHome: () => void;
  onTalk: () => void;
  onCapture: () => void;
  onOpenTool: (toolId: string) => void;
  onOpenSettings: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  return (
    <View style={styles.row}>
      <GlassFill radius={0} />
      <View style={styles.side}>
        <Pressable onPress={onOpenDrawer} style={styles.button} hitSlop={6} accessibilityLabel="Open the menu">
          <View style={styles.hamburger}>
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
          </View>
        </Pressable>
        <Pressable onPress={onBack} style={styles.button} hitSlop={6} accessibilityLabel="Back">
          <ChevronLeft size={22} color={t.goldBright} strokeWidth={2.5} />
        </Pressable>
      </View>
      <Text style={styles.title} numberOfLines={1}>{title}</Text>
      <QuickLinkButtons
        onHome={onHome}
        onTalk={onTalk}
        onCapture={onCapture}
        onOpenTool={onOpenTool}
        onOpenSettings={onOpenSettings}
        buttonStyle={styles.button}
      />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, neu }) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      backgroundColor: 'transparent',
      overflow: 'hidden',
      borderBottomWidth: 1,
      borderBottomColor: t.lineFaint,
    },
    side: { flexDirection: 'row', gap: 6 },
    button: {
      width: 38,
      height: 38,
      borderRadius: 19,
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      alignItems: 'center',
      justifyContent: 'center',
      ...neu.raisedSm,
    },
    hamburger: { width: 18, height: 13, justifyContent: 'space-between' },
    hamburgerLine: { height: 2, borderRadius: 1, backgroundColor: t.goldBright },
    title: {
      flex: 1,
      textAlign: 'center',
      fontSize: 17,
      fontWeight: '800',
      letterSpacing: 2,
      color: t.ivory,
      fontFamily: DISPLAY_FONT,
      textTransform: 'uppercase',
    },
  }),
);
